import React, { useContext, useEffect, useState } from "react";
import { base_url } from "../Utilities/apiUrl";
import { LanguageContext } from "../context/language/LanguageContext";

// The additional-payment link a support agent sends after an order changed (e.g. an upgrade to
// expedited shipping). Deliberately standalone: no basket, no token, no stepper — the ref in the
// URL is the whole session. The API's addpay.aspx owns the money; this page frames the hosted
// payment page the way the checkout does, and draws the result it posts back.
const addpayUrl = base_url.replace(/api\.aspx.*$/, "addpay.aspx");
const apiOrigin = new URL(addpayUrl, window.location.origin).origin;

// API state -> locale key. An unknown state falls back to the failure text.
const STATE_TEXT_ID = {
  paid: "addpay_state_paid",
  processing: "addpay_state_processing",
  expired: "addpay_state_expired",
  superseded: "addpay_state_superseded",
  cancelled: "addpay_state_cancelled",
  failed: "addpay_state_failed",
  not_found: "addpay_state_not_found",
};

const AddPay = () => {
  const { getTranslation } = useContext(LanguageContext);
  const ref = new URLSearchParams(window.location.search).get("ref") || "";
  const [peek, setPeek] = useState(null);

  useEffect(() => {
    if (!ref) {
      setPeek({ state: "not_found" });
      return;
    }
    fetch(`${addpayUrl}?ref=${encodeURIComponent(ref)}&peek=1`)
      .then((r) => r.json())
      .then(setPeek)
      .catch(() => setPeek({ state: "failed" }));
  }, [ref]);

  // When CreditGuard returns to addpay.aspx inside the frame, it posts
  // {addpay: {state, amount_display}} — same shape as peek. Only the API origin is trusted.
  useEffect(() => {
    const handler = (event) => {
      if (event.origin !== apiOrigin || !event.data?.addpay) return;
      setPeek(event.data.addpay);
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  if (peek?.state === "pay") {
    // A payable link goes straight to the hosted page. Same URL without peek: mints the
    // gateway session and 302s to it.
    return (
      <div style={styles.frameWrap}>
        <iframe
          title={getTranslation("addpay_frame_title")}
          src={`${addpayUrl}?ref=${encodeURIComponent(ref)}`}
          style={styles.frame}
        />
      </div>
    );
  }

  return (
    <div style={styles.wrap}>
      {!peek ? (
        <h2 style={styles.title}>{getTranslation("addpay_loading")}</h2>
      ) : (
        <>
          <h2 style={styles.title}>
            {getTranslation(
              STATE_TEXT_ID[peek.state] || STATE_TEXT_ID.failed
            )}
          </h2>
          {peek.state === "paid" && peek.amount_display && (
            <p style={styles.note}>
              {getTranslation("addpay_amount").replace(
                "{amount}",
                peek.amount_display
              )}
            </p>
          )}
        </>
      )}
    </div>
  );
};

const styles = {
  wrap: {
    textAlign: "center",
    padding: "15vh 24px 0",
    fontFamily: "inherit",
  },
  frameWrap: { height: "100vh" },
  frame: { width: "100%", height: "100%", border: 0 },
  title: { fontSize: 22, fontWeight: 600, margin: "0 0 12px" },
  note: { color: "#666", fontSize: 13, marginTop: 16 },
};

export default AddPay;
